import { toPercentString } from "src/app/shared/utils/to-percent-string";

export interface DecodedDashboardId {
  regionalId: number | undefined;
  analysisId: number;
  metricId: number;
  granularityId: number;
}

export class DashboardUtils {
  static decodeDashboardId(dashboardId: string): DecodedDashboardId {
    const dashboardIdSegments = dashboardId.split('x').map(id => +id) as [number, number, number] | [number, number, number, number];

    let regionalId
    let analysisId
    let metricId
    let granularityId

    if (dashboardIdSegments.length === 4) {
      [regionalId, analysisId, metricId, granularityId] = dashboardIdSegments;
    } else if (dashboardIdSegments.length === 3) {
      [analysisId, metricId, granularityId] = dashboardIdSegments;
    } else {
      console.error("Unexpected dashboardId format:", dashboardId);
    }

    return {
      regionalId,
      analysisId: analysisId!,
      metricId: metricId!,
      granularityId: granularityId!,
    }
  }

  /**
   * Inverse of {@link decodeDashboardId}: `{analysis}x{metric}x{granularity}`,
   * prefixed with `{regional}x` for regional dashboards. Single owner of the id format.
   */
  static encodeDashboardId({ regionalId, analysisId, metricId, granularityId }: {
    regionalId?: number;
    analysisId: number;
    metricId: number;
    granularityId: number;
  }): string {
    return [regionalId, analysisId, metricId, granularityId]
      .filter((segment) => segment !== undefined)
      .join('x');
  }

  /** Blank and non-numeric cells become null so they do not sort as zero. */
  static readNumber(raw: unknown): number | null {
    if (raw == null || raw === '') {
      return null;
    }
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  }

  /**
   * Higher number first. A missing value sorts last.
   * A blank must not become 0, or an empty district would outrank a smaller real number.
   */
  static compareDesc(a: number | null, b: number | null): number {
    const aMissing = a == null || !Number.isFinite(a);
    const bMissing = b == null || !Number.isFinite(b);
    if (aMissing && bMissing) {
      return 0;
    }
    if (aMissing) {
      return 1;
    }
    if (bMissing) {
      return -1;
    }
    return b - a;
  }

  /** Signed percent. Anonymized and negative values keep their own sign. */
  static displayYoy(yoy: string | number | null): string {
    const formatted = toPercentString(yoy);
    return formatted.includes('*') || Number(yoy) < 0 ? formatted : `+${formatted}`;
  }

}